import React, { useState, useRef } from 'react';
import { Upload, FileDown, X, CheckCircle, AlertCircle } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import './ImportModal.css';

const ImportModal = ({ isOpen, onClose, onImport, title, templateHeaders, sampleRow }) => {
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState('idle'); // idle, processing, complete, error
    const [progress, setProgress] = useState(0);
    const [summary, setSummary] = useState({ total: 0, imported: 0, duplicates: 0, errors: 0 });
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const handleDownloadTemplate = () => {
        const csvContent = "data:text/csv;charset=utf-8,"
            + templateHeaders.join(",") + "\n"
            + sampleRow.join(",");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${title.toLowerCase().replace(/\s/g, '_')}_template.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setStatus('idle');
        setProgress(0);
    };

    const processFile = () => {
        if (!file) return;

        setStatus('processing');
        const reader = new FileReader();

        reader.onload = async (event) => {
            try {
                const text = event.target.result;
                const lines = text.split('\n');
                const headers = lines[0].split(',').map(h => h.trim());

                // Validate headers roughly
                const missingHeaders = templateHeaders.filter(h => !headers.includes(h));
                if (missingHeaders.length > 2) { // Allow some mismatch but not too many
                    throw new Error(`Invalid CSV format. Missing columns: ${missingHeaders.join(', ')}`);
                }

                const data = lines.slice(1)
                    .filter(l => l.trim())
                    .map(line => {
                        const values = line.split(','); // Simple split, doesn't handle quoted commas
                        const row = {};
                        headers.forEach((header, index) => {
                            row[header] = values[index]?.trim();
                        });
                        return row;
                    });

                setSummary(prev => ({ ...prev, total: data.length }));

                // Call the actual import function passed from parent
                const result = await onImport(data, (percent) => setProgress(percent));

                setSummary({
                    total: data.length,
                    imported: result.imported,
                    duplicates: result.duplicates || 0,
                    updated: result.updated || 0,
                    errors: result.errors || 0
                });
                setStatus('complete');

            } catch (error) {
                console.error("Import error", error);
                setStatus('error');
                alert(error.message);
            }
        };

        reader.readAsText(file);
    };

    const handleClose = () => {
        setFile(null);
        setStatus('idle');
        setProgress(0);
        onClose();
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
            <div className="modal-content import-modal">
                <div className="modal-header">
                    <h3>{title}</h3>
                    <button className="close-btn" onClick={handleClose} disabled={status === 'processing'}>
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body">
                    {status === 'idle' || status === 'error' ? (
                        <div className="import-step">
                            <div className="template-section">
                                <p className="text-sm text-muted mb-2">1. Download the template to ensure correct formatting.</p>
                                <button className="btn btn-secondary btn-sm flex items-center gap-2" onClick={handleDownloadTemplate}>
                                    <FileDown size={16} /> Download CSV Template
                                </button>
                            </div>

                            <div className="upload-section mt-4">
                                <p className="text-sm text-muted mb-2">2. Upload your populated CSV file.</p>
                                <div
                                    className="dropzone"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Upload size={32} className="text-muted mb-2" />
                                    <p>{file ? file.name : "Click to select CSV file"}</p>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        accept=".csv"
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : status === 'processing' ? (
                        <div className="processing-state text-center py-8">
                            <div className="mb-4 flex justify-center">
                                <LoadingSpinner size={48} color="#d4af37" />
                            </div>
                            <h4 className="mb-2">Importing...</h4>
                            <div className="progress-bar-container">
                                <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                            </div>
                            <p className="text-sm text-muted mt-2">{Math.round(progress)}% Complete</p>
                        </div>
                    ) : (
                        <div className="complete-state text-center py-6">
                            <div className="mb-4 flex justify-center text-green">
                                <CheckCircle size={48} />
                            </div>
                            <h4>Import Complete</h4>
                            <div className="summary-stats mt-4">
                                <div className="stat-item">
                                    <span className="stat-val">{summary.imported}</span>
                                    <span className="stat-label">Imported</span>
                                </div>
                                {summary.updated !== undefined && summary.updated > 0 ? (
                                    <div className="stat-item">
                                        <span className="stat-val text-blue">{summary.updated}</span>
                                        <span className="stat-label">Updated</span>
                                    </div>
                                ) : (
                                    <div className="stat-item">
                                        <span className="stat-val text-orange">{summary.duplicates || 0}</span>
                                        <span className="stat-label">Skipped</span>
                                    </div>
                                )}
                                {summary.errors > 0 && (
                                    <div className="stat-item">
                                        <span className="stat-val text-red">{summary.errors}</span>
                                        <span className="stat-label">Errors</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    {status === 'idle' && (
                        <button
                            className="btn btn-primary"
                            disabled={!file}
                            onClick={processFile}
                        >
                            Start Import
                        </button>
                    )}
                    {(status === 'complete' || status === 'error') && (
                        <button className="btn btn-secondary" onClick={handleClose}>Close</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportModal;
